import React, { useEffect, useState, useRef } from 'react';
import { Search, Heart, Globe, Menu, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SearchOverlay } from './SearchOverlay';
import { MobileNavDrawer } from './MobileNavDrawer';
import { UserMenu } from './UserMenu';
import { useFavorites } from '../hooks/useFavorites';
import { useLanguage } from '../contexts/LanguageContext';
import { useMenuConfig } from '../contexts/MenuConfigContext';
export function TopNav() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [dropdownOpenId, setDropdownOpenId] = useState<string | null>(null);
  const { favoriteCount } = useFavorites();
  const { lang, toggleLang, ui } = useLanguage();
  const menus = useMenuConfig().config;
  const isAr = lang === 'ar';
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerSettings = menus.headerSettings || {
    logoUrlEn: "/3.svg",

    logoUrlAr: "/3.svg",

    logoHeight: 80,
    logoAltEn: 'Alex Hidden Gems',
    logoAltAr: 'أليكس هيدن جيمز'
  };
  const currentLogoUrl = isAr ?
  headerSettings.logoUrlAr || headerSettings.logoUrlEn :
  headerSettings.logoUrlEn;
  const currentLogoAlt = isAr ?
  headerSettings.logoAltAr :
  headerSettings.logoAltEn;
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node))
      {
        setDropdownOpenId(null);
      }
    };
    if (dropdownOpenId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpenId]);
  const handleEnter = (id: string) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setDropdownOpenId(id);
  };
  const handleLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setDropdownOpenId(null), 150);
  };
  return (
    <>
      <header className="fixed top-0 start-0 end-0 z-50 w-full bg-white/50 backdrop-blur-md border-b border-sea-blue/[0.08] shadow-sm supports-[backdrop-filter]:bg-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 md:h-24 flex md:grid md:grid-cols-[1fr_auto_1fr] items-center justify-between gap-4">
          {/* Logo Block — left column */}
          <div className="flex items-center justify-start shrink-0">
            <Link
              to="/"
              className="flex items-center group"
              aria-label={currentLogoAlt}>
              
              {/* Mobile gets a capped responsive height; md+ uses the admin-configured height via a CSS variable. */}
              <img
                src={currentLogoUrl}
                alt={currentLogoAlt}
                className="h-12 sm:h-16 md:h-[var(--logo-h)] w-auto transition-transform group-hover:scale-[1.02]"
                style={{
                  ['--logo-h' as any]: `${headerSettings.logoHeight}px`
                }} />
              
            </Link>
          </div>

          {/* Primary Nav Links — centered */}
          <nav className="hidden md:flex items-center justify-center gap-8">
            {menus.header.
            filter((item) => item.visible !== false).
            map((item) => {
              if (item.children && item.children.length > 0) {
                const isOpen = dropdownOpenId === item.id;
                return (
                  <div
                    key={item.id}
                    ref={isOpen ? dropdownRef : null}
                    className="relative"
                    onMouseEnter={() => handleEnter(item.id)}
                    onMouseLeave={handleLeave}>
                    
                      <button
                      onClick={() =>
                      setDropdownOpenId(isOpen ? null : item.id)
                      }
                      className="flex items-center gap-1 text-sm font-bold tracking-wide text-sea-blue hover:text-terracotta transition-colors"
                      aria-expanded={isOpen}
                      aria-haspopup="true">
                      
                        {isAr ? item.label.ar : item.label.en}
                        <ChevronDown
                        size={14}
                        className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      
                      </button>

                      {isOpen &&
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-white rounded-2xl shadow-float border border-sea-blue/10 overflow-hidden z-50">
                          <div className="py-2">
                            {item.children.
                        filter((c) => c.visible !== false).
                        map((child) => {
                          const linkProps =
                          child.type === 'external' ?
                          {
                            href: child.url,
                            target: child.openInNewTab ?
                            '_blank' :
                            undefined,
                            rel: child.openInNewTab ?
                            'noopener noreferrer' :
                            undefined
                          } :
                          {
                            to: child.url
                          };
                          const LinkComponent =
                          child.type === 'external' ? 'a' : Link;
                          return (
                            <LinkComponent
                              key={child.id}
                              {...linkProps as any}
                              onClick={() => setDropdownOpenId(null)}
                              className="block px-5 py-3 hover:bg-sand transition-colors">
                              
                                    <div
                                className={`text-sm font-bold text-sea-blue ${isAr ? 'font-arabic' : ''}`}>
                                
                                      {isAr ? child.label.ar : child.label.en}
                                    </div>
                                    {child.description && (
                              child.description.en ||
                              child.description.ar) &&
                              <div
                                className={`text-xs text-sea-blue/50 mt-0.5 ${isAr ? 'font-arabic' : ''}`}>
                                
                                          {isAr ?
                                child.description.ar :
                                child.description.en}
                                        </div>
                              }
                                  </LinkComponent>);

                        })}
                          </div>
                        </div>
                    }
                    </div>);

              }
              const linkProps =
              item.type === 'external' ?
              {
                href: item.url,
                target: item.openInNewTab ? '_blank' : undefined,
                rel: item.openInNewTab ?
                'noopener noreferrer' :
                undefined
              } :
              {
                to: item.url
              };
              const LinkComponent = item.type === 'external' ? 'a' : Link;
              return (
                <LinkComponent
                  key={item.id}
                  {...linkProps as any}
                  className={`text-sm font-bold tracking-wide text-sea-blue hover:text-terracotta transition-colors ${isAr ? 'font-arabic' : ''}`}>
                  
                    {isAr ? item.label.ar : item.label.en}
                  </LinkComponent>);

            })}
          </nav>

          {/* Actions — right column */}
          <div className="flex items-center justify-end gap-2 sm:gap-3">
            {/* Language switcher — sand pill, distinct from icons */}
            <button
              type="button"
              onClick={toggleLang}
              className="flex items-center gap-1.5 text-xs sm:text-sm font-bold text-sea-blue bg-sand hover:bg-sand-dark transition-colors px-3 py-1.5 rounded-full">
              
              <Globe size={14} className="shrink-0 text-terracotta" />
              <span className={lang === 'en' ? 'font-cairo' : ''}>
                {lang === 'en' ? 'AR' : 'EN'}
              </span>
            </button>

            {/* Action icons group — circular ghost buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSearchOpen(true)}
                className="text-sea-blue/85 hover:text-sea-blue hover:bg-sea-blue/5 transition-colors p-2 rounded-full"
                aria-label={ui.search}>
                
                <Search size={19} strokeWidth={2} />
              </button>

              <Link
                to="/favorites"
                className="text-sea-blue/85 hover:text-terracotta hover:bg-terracotta/5 transition-colors p-2 rounded-full relative"
                aria-label={ui.favorites}>
                
                <Heart size={19} strokeWidth={2} />
                {favoriteCount > 0 &&
                <span className="absolute top-0.5 right-0.5 bg-terracotta text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-white">
                    {favoriteCount}
                  </span>
                }
              </Link>
            </div>

            {/* Auth / User Menu — distinct visual treatment */}
            <div className="hidden md:block ml-1">
              <UserMenu />
            </div>

            {/* Mobile menu trigger */}
            <button
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden text-sea-blue hover:text-terracotta hover:bg-sea-blue/5 transition-colors p-2 rounded-full ml-1"
              aria-label={ui.menu}>
              
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <MobileNavDrawer
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        favoriteCount={favoriteCount} />
      
    </>);

}